import React, { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  Tooltip,
} from '@mui/material'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router'
import { getUserRoles } from 'src/mentor/layout/utils'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.04) 100%)',
  border: '1px solid rgba(116, 80, 170, 0.12)',
  color: '#7450aa',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
    borderColor: 'rgba(116, 80, 170, 0.25)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
    borderRadius: '10px',
  },
}))

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    marginTop: 8,
    minWidth: 280,
    maxWidth: 320,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(116, 80, 170, 0.1)',
    overflow: 'visible',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 20,
      width: 12,
      height: 12,
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
      border: '1px solid rgba(116, 80, 170, 0.1)',
      borderBottom: 'none',
      borderRight: 'none',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
    },
  },
}))

const MenuItemStyled = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  padding: '12px 20px',
  borderRadius: '10px',
  margin: '4px 8px',
  transition: 'all 0.2s ease',
  backgroundColor: active ? 'rgba(116, 80, 170, 0.08)' : 'transparent',
  '&:hover': {
    background: active
      ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.12) 0%, rgba(116, 80, 170, 0.08) 100%)'
      : 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.04) 100%)',
  },
  '&:first-of-type': {
    marginTop: 8,
  },
  '&:last-of-type': {
    marginBottom: 8,
  },
  '&.Mui-disabled': {
    opacity: 0.6,
  },
}))

const ActiveBadge = styled(Box)(({ theme }) => ({
  height: '22px',
  fontSize: '10px',
  fontWeight: 700,
  padding: '0 8px',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
  color: 'white',
  borderRadius: '11px',
  boxShadow: '0 2px 8px rgba(116, 80, 170, 0.3)',
}))

export default function PortalSwitcherButton() {
  const navigate = useNavigate()
  const userRoles = getUserRoles()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Get available portals based on user roles
  const availablePortals = PORTAL_CONFIG.filter((portal) =>
    portal.roles.some((role) => userRoles?.includes(role))
  )

  // Don't show if only one portal
  if (availablePortals.length <= 1) {
    return null
  }

  const handlePortalMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePortalMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePortalSwitch = (portal) => {
    handlePortalMenuClose()

    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
      } else if (portal.path === '/') {
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        localStorage.removeItem('lastChosenFlow')
        window.location.replace('/')
      } else if (portal.id === 'clinic-assistant') {
        // For Clinic Assistant portal, always navigate to /ca-home
        // The landing page will handle redirecting based on ROLE_CA_CONFIG
        // Update lastChosenFlow so login redirect respects user's portal preference
        localStorage.setItem('lastChosenFlow', 'clinic-assistant')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        navigate('/ca-home')
      } else if (portal.id === 'kiosk') {
        // For Kiosk portal, update lastChosenFlow so login redirect respects user's portal preference
        localStorage.setItem('lastChosenFlow', 'kiosk')
        navigate(portal.path)
      } else {
        // Update lastChosenFlow for other portals that use it
        if (portal.id === 'mentor') {
          localStorage.setItem('lastChosenFlow', 'mentor')
        } else if (portal.id === 'clinic') {
          localStorage.setItem('lastChosenFlow', 'clinic')
        } else if (portal.id === 'support') {
          localStorage.setItem('lastChosenFlow', 'support')
        } else if (portal.id === 'business') {
          localStorage.setItem('lastChosenFlow', 'business')
        }
        navigate(portal.path)
      }
    }, 100)
  }

  const isCurrentPortal = (portal) => {
    if (portal.external) return false

    const currentPath = window.location.pathname

    if (portal.path === '/') {
      return (
        currentPath === '/' ||
        (!currentPath.startsWith('/support-home') &&
          !currentPath.startsWith('/mentor-home') &&
          !currentPath.startsWith('/clinic-home') &&
          !currentPath.startsWith('/ca-home') &&
          !currentPath.startsWith('/kiosk') &&
          !currentPath.startsWith('/workplace'))
      )
    }

    return currentPath === portal.path || currentPath.startsWith(portal.path + '/')
  }

  return (
    <>
      <Tooltip title="Switch Portal" arrow>
        <StyledIconButton
          onClick={handlePortalMenuClick}
          size="small"
          aria-label="Switch portal"
        >
          <SwapHoriz sx={{ fontSize: 22 }} />
        </StyledIconButton>
      </Tooltip>

      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handlePortalMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {availablePortals.map((portal) => {
          const Icon = portal.icon
          const isActive = isCurrentPortal(portal)
          const shouldDisable = isActive && !portal.external && portal.path !== '/'
          return (
            <MenuItemStyled
              key={portal.id}
              active={isActive}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handlePortalSwitch(portal)
              }}
              disabled={shouldDisable}
            >
              <Box display="flex" alignItems="center" gap={1.5} width="100%">
                <Icon
                  sx={{
                    fontSize: 20,
                    color: isActive ? '#7450aa' : 'rgba(0, 0, 0, 0.6)',
                  }}
                />
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 700 : 500}
                    sx={{
                      color: isActive ? '#7450aa' : 'rgba(0, 0, 0, 0.87)',
                    }}
                  >
                    {portal.name}
                  </Typography>
                </Box>
                {isActive && (
                  <ActiveBadge>Current</ActiveBadge>
                )}
                {portal.external && (
                  <OpenInNew
                    sx={{
                      fontSize: 16,
                      color: 'rgba(0, 0, 0, 0.5)',
                    }}
                  />
                )}
              </Box>
            </MenuItemStyled>
          )
        })}
      </StyledMenu>
    </>
  )
}
